import { useEffect, useMemo, useState } from "react";
import { useFiscalStore } from "@/store/fiscal-store";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { fmtBRL, fmtNum, fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Download, Filter, RotateCcw, Save, Trash2, FileBarChart2, Search, Calendar, ListFilter, Plus, FileText, Printer } from "lucide-react";
import type { NotaSimplificada, NotaStatus } from "@/lib/fiscal-types";
import { NotaDetailModal } from "@/components/NotaDetailModal";
import { StatusBadge } from "@/components/StatusBadge";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

type SortKey = "emissao" | "emitente" | "destinatario" | "numero" | "valorOficial" | "vICMS" | "notaStatus";

interface Filtros {
  dataInicial: string; dataFinal: string; tipoData: "emissao";
  fCliente: boolean; cliente: string; fEmitente: boolean; emitente: string;
  fSerie: boolean; serie: string; fModelo: boolean; modelo: "55" | "65" | "";
  fSituacao: boolean; situacao: NotaStatus | "";
  fValorMin: boolean; valorMin: string; fValorMax: boolean; valorMax: string;
  ordenarPor: SortKey; ascendente: boolean; pageSize: number;
}

const STORAGE_KEY = "relatorio-notas-filtros";
const PAGE_SIZES = [25, 50, 100, 200];

const filtrosIniciais: Filtros = {
  dataInicial: "", dataFinal: "", tipoData: "emissao",
  fCliente: false, cliente: "", fEmitente: false, emitente: "",
  fSerie: false, serie: "", fModelo: false, modelo: "",
  fSituacao: false, situacao: "", fValorMin: false, valorMin: "", fValorMax: false, valorMax: "",
  ordenarPor: "emissao", ascendente: false, pageSize: 25,
};

function parseEmissao(s: string): number {
  if (!s) return 0;
  if (s.includes("/")) {
    const [d, m, y] = s.split(" ")[0].split("/");
    return new Date(`${y}-${m}-${d}`).getTime();
  }
  return new Date(s).getTime();
}

function emissaoISO(s: string): string {
  if (!s) return "";
  if (s.includes("/")) {
    const [d, m, y] = s.split(" ")[0].split("/");
    return `${y}-${m}-${d}`;
  }
  return s.slice(0, 10);
}

export default function RelatorioPage() {
  const notas = useFiscalStore((s) => s.notas);
  const { user } = useAuth();
  const [f, setF] = useState<Filtros>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return { ...filtrosIniciais, ...JSON.parse(raw) };
    } catch {/* ignore */ }
    return filtrosIniciais;
  });
  const [page, setPage] = useState(1);
  const [modelos, setModelos] = useState<{ id: string; nome: string; configuracao: Filtros }[]>([]);
  const [novoNome, setNovoNome] = useState("");
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(f)); }, [f]);
  useEffect(() => { loadModelos(); }, [user?.id]);

  async function loadModelos() {
    if (!user) return;
    const { data, error } = await supabase.from("modelos_relatorio").select("id, nome, configuracao").order("created_at", { ascending: false });
    if (!error) setModelos((data || []) as any);
  }

  async function salvarModelo() {
    if (!user) { toast.error("Faça login para salvar"); return; }
    const nome = novoNome.trim();
    if (!nome) { toast.error("Informe um nome"); return; }
    const { error } = await supabase.from("modelos_relatorio").insert({ user_id: user.id, nome, configuracao: f as any });
    if (!error) { toast.success("Modelo salvo"); setNovoNome(""); loadModelos(); }
  }

  const filtradas = useMemo(() => {
    const ini = f.dataInicial ? new Date(f.dataInicial).getTime() : -Infinity;
    const fim = f.dataFinal ? new Date(f.dataFinal).getTime() + 86_399_999 : Infinity;
    const vMin = f.fValorMin && f.valorMin !== "" ? Number(f.valorMin) : -Infinity;
    const vMax = f.fValorMax && f.valorMax !== "" ? Number(f.valorMax) : Infinity;
    const cliente = f.fCliente ? f.cliente.trim().toLowerCase() : "";
    const emitente = f.fEmitente ? f.emitente.trim().toLowerCase() : "";
    const out = notas.filter(n => {
      const t = parseEmissao(n.emissao);
      if (t < ini || t > fim) return false;
      const v = n.valorOficial || 0;
      if (v < vMin || v > vMax) return false;
      if (cliente && !(n.destinatario || "").toLowerCase().includes(cliente)) return false;
      if (emitente && !(n.emitente || "").toLowerCase().includes(emitente)) return false;
      if (f.fSerie && String(n.serie) !== f.serie) return false;
      if (f.fModelo && f.modelo && n.modelo !== Number(f.modelo)) return false;
      if (f.fSituacao && f.situacao && n.notaStatus !== f.situacao) return false;
      return true;
    });
    const dir = f.ascendente ? 1 : -1;
    out.sort((a, b) => {
      const av: any = f.ordenarPor === "emissao" ? parseEmissao(a.emissao) : (a as any)[f.ordenarPor];
      const bv: any = f.ordenarPor === "emissao" ? parseEmissao(b.emissao) : (b as any)[f.ordenarPor];
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av ?? "").localeCompare(String(bv ?? ""), "pt-BR") * dir;
    });
    return out;
  }, [notas, f]);

  useEffect(() => { setPage(1); }, [f]);

  const totals = useMemo(() => {
    let valor = 0, icms = 0, pis = 0, cofins = 0;
    filtradas.forEach(n => {
      valor += n.valorOficial || 0; icms += n.vICMS || 0; pis += n.vPIS || 0; cofins += n.vCOFINS || 0;
    });
    return { qtd: filtradas.length, valor, icms, pis, cofins };
  }, [filtradas]);

  const totalPages = Math.max(1, Math.ceil(filtradas.length / f.pageSize));
  const pageData = filtradas.slice((page - 1) * f.pageSize, page * f.pageSize);

  function exportarCSV() {
    if (!filtradas.length) return;
    const headers = ["Chave", "Modelo", "Numero", "Serie", "Emissao", "Emitente", "CNPJ Emit", "Destinatario", "Valor Oficial", "ICMS", "PIS", "COFINS", "Situacao"];
    const rows = filtradas.map(n => [n.chave, n.modelo, n.numero, n.serie, emissaoISO(n.emissao), n.emitente, n.cnpjEmit, n.destinatario, (n.valorOficial || 0).toFixed(2), (n.vICMS || 0).toFixed(2), (n.vPIS || 0).toFixed(2), (n.vCOFINS || 0).toFixed(2), n.notaStatus]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `relatorio-notas-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  function exportarPDF() {
    if (!filtradas.length) return;
    const doc = new jsPDF("l", "mm", "a4");
    const m = 10;
    const contentW = doc.internal.pageSize.width - (m * 2);

    // Header do PDF
    doc.setFillColor(245, 245, 245); doc.roundedRect(m, m, contentW, 20, 2, 2, "F");
    doc.setFontSize(14); doc.setFont("helvetica", "bold"); doc.text("RELATÓRIO CONSOLIDADO DE DOCUMENTOS DE DADOS", m + 5, m + 10);
    doc.setFontSize(8); doc.setFont("helvetica", "normal");
    doc.text(`Extraído em: ${new Date().toLocaleString("pt-BR")}`, m + 5, m + 16);
    doc.text(`Período: ${f.dataInicial || "Início"} até ${f.dataFinal || "Fim"}`, contentW - 50, m + 16);

    autoTable(doc, {
      startY: 35,
      theme: "grid",
      head: [["EMISSÃO", "NÚMERO", "SÉRIE", "EMITENTE", "DESTINATÁRIO", "VALOR TOTAL", "ICMS", "PIS", "COFINS", "STATUS"]],
      body: filtradas.map(n => [
        n.emissao?.split(" ")[0], n.numero, n.serie,
        (n.emitente || "").slice(0, 30), (n.destinatario || "").slice(0, 30),
        fmtBRL(n.valorOficial || 0), fmtBRL(n.vICMS || 0), fmtBRL(n.vPIS || 0), fmtBRL(n.vCOFINS || 0), n.notaStatus
      ]),
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [26, 102, 204], textColor: [255, 255, 255], fontStyle: "bold" },
      columnStyles: { 5: { halign: "right" }, 6: { halign: "right" }, 7: { halign: "right" }, 8: { halign: "right" } }
    });

    const finalY = (doc as any).lastAutoTable.finalY + 10;
    doc.setFillColor(240, 240, 240); doc.roundedRect(m, finalY, contentW, 15, 2, 2, "F");
    doc.setFontSize(9); doc.setFont("helvetica", "bold");
    doc.text(`TOTAL DE NOTAS: ${totals.qtd}`, m + 5, finalY + 9);
    doc.text(`VALOR TOTAL: ${fmtBRL(totals.valor)}`, m + 60, finalY + 9);
    doc.text(`ICMS TOTAL: ${fmtBRL(totals.icms)}`, m + 130, finalY + 9);
    doc.text(`PIS/COFINS: ${fmtBRL(totals.pis + totals.cofins)}`, contentW - 60, finalY + 9);

    doc.save(`relatorio_dados_${new Date().getTime()}.pdf`);
    toast.success("Relatório PDF gerado com sucesso.");
  }

  if (!notas.length) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <div className="glass-card py-24 text-center space-y-4">
          <FileBarChart2 className="size-16 mx-auto text-primary opacity-20" />
          <h2 className="text-2xl font-bold dark:text-white">Nenhum dado para exibir</h2>
          <p className="text-sm text-muted-foreground max-w-sm mx-auto">Faça o upload dos seus arquivos XML para desbloquear as ferramentas de relatórios avançados.</p>
          <Button variant="outline" className="mt-4 rounded-xl border-white/10" onClick={() => window.location.href = '/upload'}>Ir para Upload</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-8 py-8 max-w-[1600px] mx-auto space-y-10 animate-in fade-in duration-500">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl lg:text-3xl font-extrabold tracking-tight dark:text-white text-slate-900">Relatório Consolidado</h1>
          <p className="text-xs lg:text-sm text-muted-foreground mt-1">
            Análise detalhada de {fmtNum(filtradas.length)} documentos processados.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 px-3 lg:px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-xl w-fit">
            <FileText className="size-4 text-blue-600 dark:text-blue-400" />
            <span className="text-xs lg:text-sm font-medium text-blue-900 dark:text-blue-200">Filtrado: {fmtNum(filtradas.length)}</span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setF(filtrosIniciais)} className="rounded-xl border-slate-200 dark:border-white/10 gap-2 bg-white/5 backdrop-blur-md">
              <RotateCcw className="size-3.5" /> <span className="hidden sm:inline">Resetar</span>
            </Button>
            <Button variant="outline" size="sm" onClick={exportarCSV} className="rounded-xl gap-2 border-slate-200 dark:border-white/10 bg-white/5 backdrop-blur-md">
              <Download className="size-3.5" /> <span className="hidden sm:inline">CSV</span>
            </Button>
            <Button size="sm" onClick={exportarPDF} className="rounded-xl gap-2 shadow-lg shadow-blue-500/20 bg-blue-600 hover:bg-blue-700 text-white border-none font-bold">
              <Printer className="size-3.5" /> PDF
            </Button>
          </div>
        </div>
      </header>

      {/* Grid de Filtros Glass */}
      <div className="grid lg:grid-cols-4 gap-8">
        <div className="lg:col-span-3 space-y-6">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="glass-card p-6 space-y-5 border-blue-500/10 overflow-hidden relative">
              <h3 className="text-xs font-black uppercase tracking-[0.2em] text-blue-500 flex items-center gap-2">
                <Calendar className="size-4" /> Período de Análise
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">Data Inicial</Label>
                  <Input
                    type="date"
                    value={f.dataInicial}
                    onChange={(e) => setF({ ...f, dataInicial: e.target.value })}
                    className="bg-slate-950/40 border-white/10 text-white focus:border-blue-500/50 h-10 [color-scheme:dark] rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">Data Final</Label>
                  <Input
                    type="date"
                    value={f.dataFinal}
                    onChange={(e) => setF({ ...f, dataFinal: e.target.value })}
                    className="bg-slate-950/40 border-white/10 text-white focus:border-blue-500/50 h-10 [color-scheme:dark] rounded-xl"
                  />
                </div>
              </div>
            </div>

            <div className="glass-card p-5 space-y-4 border-blue-500/10">
              <h3 className="text-xs font-black uppercase tracking-[0.2em] text-blue-500 flex items-center gap-2"><Search className="size-3.5" /> Filtros Avançados</h3>
              <div className="space-y-4">
                <FiltroInline checked={f.fCliente} onCheck={(v) => setF({ ...f, fCliente: v })} label="Destinatário">
                  <Input value={f.cliente} disabled={!f.fCliente} onChange={(e) => setF({ ...f, cliente: e.target.value })} placeholder="Nome ou CNPJ do cliente..." className="h-9 text-xs bg-slate-900/40 border-white/10 text-white placeholder:text-slate-600" />
                </FiltroInline>
                <FiltroInline checked={f.fEmitente} onCheck={(v) => setF({ ...f, fEmitente: v })} label="Emitente">
                  <Input value={f.emitente} disabled={!f.fEmitente} onChange={(e) => setF({ ...f, emitente: e.target.value })} placeholder="Nome ou CNPJ do emitente..." className="h-9 text-xs bg-slate-900/40 border-white/10 text-white placeholder:text-slate-600" />
                </FiltroInline>
              </div>
            </div>

            <div className="glass-card p-6 space-y-5 border-blue-500/10">
              <h3 className="text-xs font-black uppercase tracking-[0.2em] text-blue-500 flex items-center gap-2">
                <ListFilter className="size-4" /> Ordenação Dinâmica
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">Ordenar Por</Label>
                  <Select value={f.ordenarPor} onValueChange={(v) => setF({ ...f, ordenarPor: v as SortKey })}>
                    <SelectTrigger className="h-10 text-xs bg-blue-500/10 border-blue-500/20 text-white hover:bg-blue-500/20 transition-all rounded-xl shadow-inner">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900/95 border-blue-500/20 backdrop-blur-xl">
                      <SelectItem value="emissao">Data de Emissão</SelectItem>
                      <SelectItem value="valorOficial">Valor Total</SelectItem>
                      <SelectItem value="numero">Número da Nota</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block">Sentido</Label>
                  <Select value={f.ascendente ? "asc" : "desc"} onValueChange={(v) => setF({ ...f, ascendente: v === "asc" })}>
                    <SelectTrigger className="h-10 text-xs bg-blue-500/10 border-blue-500/20 text-white hover:bg-blue-500/20 transition-all rounded-xl shadow-inner">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900/95 border-blue-500/20 backdrop-blur-xl">
                      <SelectItem value="asc">Crescente</SelectItem>
                      <SelectItem value="desc">Decrescente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          {/* Tabela Uniformizada */}
          <div className="glass-card overflow-hidden border-white/5 shadow-2xl bg-slate-900/10">
            <Table>
              <TableHeader className="bg-slate-50/5 dark:bg-slate-900/50">
                <TableRow className="text-muted-foreground border-b border-slate-200 dark:border-white/10 hover:bg-transparent">
                  <TableHead className="p-4 font-bold text-[10px] uppercase">Chave de Acesso</TableHead>
                  <TableHead className="p-4 font-bold text-[10px] uppercase">Data Emissão</TableHead>
                  <TableHead className="p-4 font-bold text-[10px] uppercase">Nº / Série</TableHead>
                  <TableHead className="p-4 font-bold text-[10px] uppercase">Emitente</TableHead>
                  <TableHead className="p-4 font-bold text-[10px] uppercase text-right">Valor Total</TableHead>
                  <TableHead className="p-4 font-bold text-[10px] uppercase text-center">Situação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="divide-y divide-slate-200 dark:divide-white/5">
                {pageData.map((n, idx) => (
                  <TableRow key={n.chave + n.numero} className="cursor-pointer hover:bg-primary/5 transition-colors group" onClick={() => setOpenIndex((page - 1) * f.pageSize + idx)}>
                    <TableCell className="p-4 font-mono text-[10px] text-slate-500">...{n.chave.slice(-12)}</TableCell>
                    <TableCell className="p-4 text-[11px] text-slate-600 dark:text-slate-400 tabular-nums">
                      {fmtDate(n.emissao)}
                    </TableCell>
                    <TableCell className="p-4">
                      <span className="text-[10px] font-bold text-slate-800 dark:text-white tabular-nums">
                        {n.numero} <span className="opacity-40">/</span> {n.serie}
                      </span>
                    </TableCell>
                    <TableCell className="p-4 max-w-[280px] truncate text-[11px] font-medium text-slate-700 dark:text-slate-300">{n.emitente}</TableCell>
                    <TableCell className="p-4 text-right text-xs font-black text-emerald-600 dark:text-emerald-400 tabular-nums">{fmtBRL(n.valorOficial || 0)}</TableCell>
                    <TableCell className="p-4 text-center">
                      <StatusBadge status={n.notaStatus} size="sm" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter className="bg-slate-950/90 border-t-2 border-blue-900/50">
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={4} className="font-black text-[10px] text-blue-500 uppercase tracking-[0.2em] px-8 py-6 border-r border-white/10">Totais Consolidados ({fmtNum(totals.qtd)} notas)</TableCell>
                  <TableCell className="text-right font-black text-white text-xl tabular-nums px-8 py-6 dark:text-glow-blue border-r border-white/10">{fmtBRL(totals.valor)}</TableCell>
                  <TableCell />
                </TableRow>
              </TableFooter>
            </Table>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-2">
            <div className="flex items-center gap-4 px-4 py-2 rounded-2xl bg-white/[0.03] border border-white/5">
              <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Exibição por página</span>
              <Select value={String(f.pageSize)} onValueChange={(v) => setF({ ...f, pageSize: Number(v) })}>
                <SelectTrigger className="h-8 w-20 text-[10px] font-black bg-white/10 border-white/20 text-white rounded-xl shadow-inner hover:bg-white/20 transition-all">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-white/10 shadow-2xl bg-slate-900/95 backdrop-blur-xl">
                  {PAGE_SIZES.slice(0, 3).map(size => (
                    <SelectItem key={size} value={String(size)} className="text-[10px] font-bold">{size}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-6">
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                Mostrando <span className="text-white">{(page - 1) * f.pageSize + 1}</span> - <span className="text-white">{Math.min(page * f.pageSize, filtradas.length)}</span> de <span className="text-white">{filtradas.length}</span>
              </span>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                  className="h-10 px-6 rounded-2xl border-white/10 bg-white/5 text-white hover:bg-white/10 hover:text-white transition-all font-bold shadow-xl disabled:opacity-10"
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                  className="h-10 px-6 rounded-2xl border-white/20 bg-white/15 text-white hover:bg-white/25 hover:text-white transition-all font-bold shadow-xl shadow-white/5 disabled:opacity-10"
                >
                  Próxima
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-8">
          {/* Modelos Salvos Glass */}
          <div className="glass-card p-7 space-y-6 border-blue-500/10 shadow-2xl relative overflow-hidden group min-h-[300px] flex flex-col">
            <div className="absolute -right-6 -top-6 opacity-[0.03] rotate-12 group-hover:opacity-10 transition-all duration-500">
              <Save className="size-32" />
            </div>
            <h3 className="text-xs font-black uppercase tracking-[0.25em] text-blue-500 flex items-center gap-3">
              <Save className="size-4" /> Modelos Salvos
            </h3>
            <div className="flex gap-2 relative z-10">
              <Input placeholder="Salvar configuração..." value={novoNome} onChange={(e) => setNovoNome(e.target.value)} className="h-11 text-xs bg-slate-900/60 border-white/10 text-white focus:border-blue-500/50 rounded-xl" />
              <Button onClick={salvarModelo} className="size-11 rounded-xl bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/20 p-0 flex items-center justify-center">
                <Plus className="size-5" />
              </Button>
            </div>
            <div className="space-y-2 flex-1 overflow-auto pr-2 scrollbar-thin relative z-10">
              {modelos.map((m) => (
                <div key={m.id} className="flex items-center justify-between p-4 rounded-2xl border border-white/5 bg-white/[0.03] hover:bg-blue-500/10 hover:border-blue-500/20 transition-all group/item">
                  <button onClick={() => setF({ ...filtrosIniciais, ...m.configuracao })} className="text-xs font-bold text-slate-300 group-hover/item:text-white text-left truncate flex-1">{m.nome}</button>
                  <button onClick={() => { supabase.from("modelos_relatorio").delete().eq("id", m.id).then(() => loadModelos()); }} className="opacity-0 group-hover/item:opacity-100 p-2 text-red-400 hover:bg-red-400/10 rounded-xl transition-all"><Trash2 className="size-4" /></button>
                </div>
              ))}
              {modelos.length === 0 && (
                <div className="flex flex-col items-center justify-center py-10 opacity-30">
                  <Save className="size-10 mb-2" />
                  <p className="text-[10px] font-bold uppercase tracking-widest">Nenhum modelo salvo</p>
                </div>
              )}
            </div>
          </div>

          <div className="glass-card p-7 space-y-7 border-emerald-500/10 shadow-2xl bg-gradient-to-br from-emerald-500/[0.03] to-transparent">
            <h3 className="text-xs font-black uppercase tracking-[0.25em] text-emerald-500 flex items-center gap-3">
              <FileText className="size-4" /> Resumo Financeiro
            </h3>
            <div className="space-y-6">
              <div className="flex justify-between items-center pb-5 border-b border-white/5 group">
                <div className="space-y-1">
                  <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest opacity-60">ICMS Acumulado</span>
                  <div className="text-xl font-black text-white dark:text-glow-sm tabular-nums tracking-tight">{fmtBRL(totals.icms)}</div>
                </div>
                <Badge variant="outline" className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20 text-[9px] font-black px-2 py-1 rounded-lg">DÉBITO</Badge>
              </div>
              <div className="flex justify-between items-center pb-5 border-b border-white/5 group">
                <div className="space-y-1">
                  <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest opacity-60">PIS/COFINS</span>
                  <div className="text-xl font-black text-white dark:text-glow-sm tabular-nums tracking-tight">{fmtBRL(totals.pis + totals.cofins)}</div>
                </div>
                <Badge variant="outline" className="bg-sky-500/10 text-sky-500 border-sky-500/20 text-[9px] font-black px-2 py-1 rounded-lg">FEDERAIS</Badge>
              </div>
              <div className="pt-4 space-y-2">
                <span className="text-[10px] font-black text-blue-500 uppercase tracking-[0.3em] block">Total Bruto Filtrado</span>
                <div className="text-4xl font-black text-blue-500 dark:text-glow-blue tabular-nums tracking-tighter leading-none">{fmtBRL(totals.valor)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <NotaDetailModal notas={filtradas} index={openIndex} onClose={() => setOpenIndex(null)} onChangeIndex={setOpenIndex} />
    </div>
  );
}

function FiltroInline({ checked, onCheck, label, children }: any) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <Checkbox checked={checked} onCheckedChange={(v) => onCheck(!!v)} className="size-3.5 border-white/20 bg-white/5" />
        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none cursor-pointer">{label}</label>
      </div>
      {children}
    </div>
  );
}
